/**
 * O RATEIO DO GRUPO, como a API devolve (`GET /clients/:id/rateio`), e as
 * leituras que a tela faz dele.
 *
 * ⛔ A FONTE É A API, NÃO O NOME DO ITEM. Até 01/10/2026 o percentual só existia
 * como texto no nome ("Licença de Sistema IPCOM Cloud (rateio 68,89%)"). O
 * backend (#281, #282) passou a guardar o rateio como dado, no grupo, versionado
 * por competência. Nada aqui lê o nome.
 *
 * ⛔ NUNCA RECALCULE VALOR A PARTIR DO PERCENTUAL EXIBIDO. A produção cobra os
 * R$ 449,00 como 309,31 + 139,69 (= 449 × 31/45 e × 14/45); pelos 68,89%
 * arredondados daria 309,32 + 139,68. O percentual é apresentação; o peso é a
 * verdade, e o valor certo já vem pronto da API.
 *
 * ⚠️ `baseDoPercentual` muda o significado do número: a versão atual da
 * PARANAFRIGOR é sobre o POOL COMPARTILHADO (R$ 438,50); a de 08/2026 era sobre o
 * TOTAL DO GRUPO (R$ 727,10). A tela diz a base, sempre, junto do percentual.
 */

export type CriterioDeRateio = 'ramais_contratados' | 'percentual_fixo' | 'partes_iguais';
export type BaseDoPercentual = 'pool_compartilhado' | 'total_do_grupo';

/**
 * ⛔ TRÊS NÚMEROS, TRÊS PERGUNTAS (contrato do backend de 05/10/2026). Até então a API
 * mandava `valorVigente`, e ele carregava o COBRADO (soma dos itens) com nome de
 * ACORDADO: na CONCEITO dizia 352,91 quando a versão 10/2026 acorda 311,39, e o aviso
 * de acordado × cobrado nunca aparecia. O campo deixou de existir, de propósito.
 *  - `valorAcordado`: o que a versão registrou para a empresa (null = não registrou;
 *    nunca zero);
 *  - `valorCobrado`: a soma dos itens do pool hoje;
 *  - `valorCalculado`: o que a base de hoje daria.
 * E dois deltas, duas perguntas: `deltaAcordadoVsCobrado` ("o cobrado honra o
 * acordo?", null sem acordado) e `deltaReais` ("a base mudou?", calculado − cobrado).
 */
export interface LinhaDoRateio {
  clientId: number;
  /** Percentual ACORDADO na versão. */
  percentualVigente: number;
  percentualCalculado: number;
  valorAcordado: number | null;
  valorCobrado: number;
  valorCalculado: number;
  /** cobrado − acordado; null quando a versão não registrou o acordado. */
  deltaAcordadoVsCobrado: number | null;
  /** calculado − cobrado. */
  deltaReais: number;
}

export interface ItemDoRateio {
  productId: number;
  productCode: string | null;
  name: string | null;
  valorCheio: number;
  porEmpresa: Array<{ clientId: number; percentual: number; valor: number }>;
}

export interface VersaoDoRateio {
  id: number;
  competenciaInicio: string | null;
  criterio: CriterioDeRateio;
  baseDoPercentual: BaseDoPercentual;
  poolSnapshot: number | null;
  descontosSeguemRateio?: boolean;
  observacao?: string | null;
  /**
   * As listas DESTA versão (backend #326, nomes alinhados em 06/10/2026). `null` = a versão não as
   * registrou: a tela mostra `listasNaoRegistradasMotivo` como vem, nunca as listas de hoje.
   */
  produtoIds?: number[] | null;
  produtoIdsBase?: number[] | null;
  /** Para a tela DECIDIR. */
  listasRegistradas?: boolean;
  /** Para a tela DIZER: texto pronto do backend. */
  listasNaoRegistradasMotivo?: string | null;
}

export interface SemRateio {
  matrizId: number;
  competencia: string;
  temRateio: false;
  motivo: string;
  versoes?: VersaoDoRateio[];
}

export interface ComRateio {
  matrizId: number;
  competencia: string;
  temRateio: true;
  versaoId: number;
  competenciaInicio: string | null;
  criterio: CriterioDeRateio;
  baseDoPercentual: BaseDoPercentual;
  poolSnapshot: number | null;
  descontosSeguemRateio: boolean;
  /** null no ramo histórico quando a versão não registrou as listas: mostre o motivo, nunca as de hoje. */
  produtoIds: number[] | null;
  produtoIdsBase: number[] | null;
  listasRegistradas?: boolean;
  listasNaoRegistradasMotivo?: string | null;
  /**
   * O modelo do que se divide, DECLARADO no backend (nunca inferido da forma): `composto` = soma de
   * serviços reais, editável; `afirmado` = um valor afirmado numa linha (o AJUSTE-RATEIO), não editável;
   * `null` = ainda não declarado, também não editável.
   */
  modeloDoPool?: 'composto' | 'afirmado' | null;
  observacao: string | null;
  pool: number;
  somaVigenteFecha: boolean;
  somaVigente: number;
  motivoDoCalculo: string | null;
  divergente: boolean;
  base: Array<{ clientId: number; ramais: number }>;
  linhas: LinhaDoRateio[];
  itens: { vigente: ItemDoRateio[]; calculada: ItemDoRateio[] };
  versoes: VersaoDoRateio[];
}

export type RateioDoGrupo = SemRateio | ComRateio;

/**
 * Leva a resposta do GET ao contrato novo. O backend sobe DEPOIS do front, então a
 * tela convive com as duas formas: na antiga, `valorVigente` era o COBRADO (a soma dos
 * itens), e o acordado não vinha; ele fica null, e nada é afirmado sobre o acordo.
 */
export function normalizarRateio(r: any): RateioDoGrupo {
  if (!r || r.temRateio === false || !Array.isArray(r.linhas)) return r;
  const linhas: LinhaDoRateio[] = r.linhas.map((l: any) => {
    if ('valorCobrado' in l) return l as LinhaDoRateio;
    const { valorVigente, ...resto } = l;
    return {
      ...resto,
      valorAcordado: null,
      valorCobrado: Number(valorVigente) || 0,
      deltaAcordadoVsCobrado: null,
    } as LinhaDoRateio;
  });
  return { ...r, linhas };
}

export const TEXTO_DO_CRITERIO: Record<CriterioDeRateio, string> = {
  ramais_contratados: 'por ramais contratados',
  percentual_fixo: 'percentual fixo acordado',
  partes_iguais: 'em partes iguais',
};

export const TEXTO_DA_BASE: Record<BaseDoPercentual, string> = {
  pool_compartilhado: 'sobre o que o grupo compartilha',
  total_do_grupo: 'sobre o total do grupo',
};

/** 68.89 → "68,89%". Só apresentação. */
export const fmtPercentual = (p: number) =>
  `${Number(p).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

/** A versão devolvida é a mais recente (a que vale hoje)? */
export function ehVersaoAtual(r: ComRateio): boolean {
  const ultima = [...(r.versoes || [])]
    .sort((a, b) => String(a.competenciaInicio || '').localeCompare(String(b.competenciaInicio || '')))
    .pop();
  return !ultima || ultima.id === r.versaoId;
}

/**
 * O valor dividido nesta versão. Na atual, o pool de hoje; numa versão passada,
 * o `poolSnapshot` (o que foi dividido naquela época).
 *
 * ⚠️ Numa versão passada a tela NÃO mostra reais por empresa: os valores que a
 * API devolve vêm dos contratos de HOJE, e montar o valor histórico a partir do
 * percentual seria justamente o recálculo que não se faz. Mostra percentual,
 * critério, base e o pool da época.
 */
export function poolDaVersao(r: ComRateio): number {
  return ehVersaoAtual(r) ? r.pool : (r.poolSnapshot ?? r.pool);
}

/** Total de ramais que serve de base ao critério `ramais_contratados`. */
export const totalDeRamais = (r: ComRateio) => r.base.reduce((s, b) => s + (Number(b.ramais) || 0), 0);

export interface FatiaDaEmpresa {
  /** Percentual ACORDADO na versão. */
  percentual: number;
  /** O que a versão registrou; null quando não registrou. */
  acordado: number | null;
  /** A soma dos itens do pool hoje. */
  cobrado: number;
  /** cobrado − acordado; null sem acordado. */
  deltaAcordadoVsCobrado: number | null;
  /** Competência da versão que sustenta o percentual acordado ("2026-10"). */
  versao: string | null;
  /**
   * HOJE, separado do acordado: o que o critério daria pela base atual. Não é o
   * porquê do percentual acordado: a versão pode ter sido acordada com outra base
   * (CONCEITO, 05/10/2026: 60,78% = 31 de 51; a base de hoje é 31 de 45).
   */
  hoje: { percentual: number; base: string | null };
}

/** A fatia de uma empresa: o acordado na versão e o cobrado hoje, como vieram da API. */
export function fatiaDaEmpresa(r: ComRateio, clientId: number): FatiaDaEmpresa | null {
  const linha = r.linhas.find((l) => l.clientId === clientId);
  if (!linha) return null;
  // ⛔ Até 05/10/2026 havia aqui um "porquê" ("por ter 31 dos 45 ramais") colado no
  // percentual ACORDADO. Era falso: 60,78% é 31 de 51, a base da época do acordo, e a
  // API não expõe essa base (virá com as listas por versão). A base de HOJE vai em
  // afirmação separada, ao lado do percentual de que ela é verdade.
  let base: string | null = null;
  if (r.criterio === 'ramais_contratados') {
    const meus = r.base.find((b) => b.clientId === clientId)?.ramais ?? 0;
    base = `${meus} de ${totalDeRamais(r)} ramais`;
  } else if (r.criterio === 'partes_iguais') {
    base = `partes iguais entre ${r.linhas.length} empresas`;
  }
  return {
    percentual: linha.percentualVigente,
    acordado: linha.valorAcordado,
    cobrado: linha.valorCobrado,
    deltaAcordadoVsCobrado: linha.deltaAcordadoVsCobrado,
    versao: r.competenciaInicio,
    hoje: { percentual: linha.percentualCalculado, base },
  };
}

export interface SeloDoItem {
  percentual: number;
  valorCheio: number;
}

/**
 * O selo "68,89% de 449,00" de um item, para a empresa dada. `null` quando o
 * produto não é rateado no grupo.
 *
 * ⛔ SEM VALOR EM REAIS. Medido em produção (02/10/2026): `itens.vigente[].
 * porEmpresa[].valor` vem do percentual arredondado × valor cheio (309,32 /
 * 139,68), não do que está cobrado (309,31 / 139,69). O valor cobrado é o
 * total da própria linha do item, que a tela já mostra.
 */
export function seloDoItem(r: ComRateio, clientId: number, productId: number): SeloDoItem | null {
  const item = r.itens.vigente.find((i) => i.productId === Number(productId));
  if (!item) return null;
  const fatia = item.porEmpresa.find((p) => p.clientId === clientId);
  if (!fatia) return null;
  return { percentual: fatia.percentual, valorCheio: item.valorCheio };
}

export type GravidadeDoAviso = 'erro' | 'proposta';

export interface AvisoDoRateio {
  gravidade: GravidadeDoAviso;
  titulo: string;
  linhas: string[];
}

/**
 * O que a tela precisa avisar.
 *  - `somaVigenteFecha: false` é ERRO: a divisão cobrada não fecha 100%. Foi o
 *    estado que custou R$ 38,18 em agosto, quando a TECH-FIN saiu.
 *  - `divergente: true` NÃO é erro: a base mudou (alguém ganhou ou perdeu ramal) e
 *    o critério hoje daria outra divisão. É uma PROPOSTA de recálculo, que só vale
 *    com confirmação humana (a tela de aplicar ainda não existe).
 */
export function avisosDoRateio(r: ComRateio, nome: (clientId: number) => string): AvisoDoRateio[] {
  const avisos: AvisoDoRateio[] = [];
  // Versão passada: a regra antiga comparada com a base de HOJE diverge por
  // definição (a PARANAFRIGOR de 08/2026 volta `divergente: true` porque a
  // TECH-FIN saiu). Não é recálculo pendente; não se avisa.
  if (!ehVersaoAtual(r)) return avisos;
  if (!r.somaVigenteFecha) {
    avisos.push({
      gravidade: 'erro',
      titulo: `A divisão cobrada soma ${fmtPercentual(r.somaVigente)}, não 100%`,
      linhas: ['Parte do compartilhado não está sendo cobrada de ninguém, ou está sendo cobrada a mais. Confira antes de faturar.'],
    });
  }
  if (r.divergente) {
    // Quem muda é quem muda de PERCENTUAL. O `deltaReais` é magnitude, e estimada (cálculo
    // contra valor distribuído à mão); com ele de filtro, um arredondamento escondia a empresa.
    const mudam = r.linhas.filter((l) => Math.round(l.percentualVigente * 100) !== Math.round(l.percentualCalculado * 100));
    avisos.push({
      gravidade: 'proposta',
      titulo: `O critério (${TEXTO_DO_CRITERIO[r.criterio]}) hoje daria outra divisão`,
      linhas: mudam.map((l) =>
        `${nome(l.clientId)}: ${fmtPercentual(l.percentualVigente)} → ${fmtPercentual(l.percentualCalculado)} (estimativa contra o cobrado: ${l.deltaReais > 0 ? '+' : '−'}R$ ${Math.abs(l.deltaReais).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} por mês)`,
      ),
    });
  }
  return avisos;
}

/** 'YYYY-MM' do mês anterior. */
function mesAnterior(c: string): string {
  const [a, m] = c.split('-').map(Number);
  return m === 1 ? `${a - 1}-12` : `${a}-${String(m - 1).padStart(2, '0')}`;
}

/**
 * As versões para o seletor, da mais nova para a mais antiga, cada uma com a
 * competência a pedir à API para vê-la.
 *
 * ⚠️ Versão com início NÃO REGISTRADO (`competenciaInicio: null`) existe: é a
 * antiga da PARANAFRIGOR (11/11/34/22/22 sobre R$ 727,10). Para pedi-la, usa-se
 * o mês anterior ao início da versão seguinte. Pedir sem competência traria a
 * atual de volta.
 */
export function versoesParaSeletor(versoes: VersaoDoRateio[]): Array<VersaoDoRateio & { competenciaParaVer: string | null; atual: boolean }> {
  const asc = [...versoes].sort((a, b) => String(a.competenciaInicio || '').localeCompare(String(b.competenciaInicio || '')));
  return asc
    .map((v, i) => {
      const proxima = asc[i + 1];
      const atual = i === asc.length - 1;
      const competenciaParaVer = atual
        ? null
        : v.competenciaInicio || (proxima?.competenciaInicio ? mesAnterior(proxima.competenciaInicio) : null);
      return { ...v, competenciaParaVer, atual };
    })
    .reverse();
}
